import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAlert } from '../../hooks/useAlert';
import EditMeetingModal from './EditMeetingModal'; // 1. Import the new modal

// MeetingRow component now manages the state for its own edit modal
const MeetingRow = ({ meeting, onUpdate }) => {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false); // 2. Add state for the modal

    const handleStateChange = async (newState) => {
        if (!window.confirm(`Are you sure you want to set this meeting to '${newState}'?`)) {
            return;
        }
        try {
            await api.put(`/admin/meetings/${meeting.meet_id}/state`, { state: newState });
            showAlert(`Meeting successfully set to ${newState}.`, 'success');
            onUpdate();
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to update meeting state.');
        }
    };

    const styles = {
        cell: { padding: '0.8rem', borderBottom: '1px solid #374151' },
        button: { marginRight: '0.5rem', padding: '0.3rem 0.6rem', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    };

    return (
        <>
            {/* 3. Conditionally render the modal */}
            {isEditModalOpen && (
                <EditMeetingModal 
                    meeting={meeting} 
                    onClose={() => setIsEditModalOpen(false)} 
                    onUpdate={onUpdate} 
                />
            )}
            <tr>
                <td style={styles.cell}>
                    <strong>{meeting.name}</strong><br />
                    <small style={{ color: '#9CA3AF' }}>ID: {meeting.meet_id}</small>
                </td>
                <td style={styles.cell}>{meeting.creator_name}</td>
                <td style={{ ...styles.cell, textTransform: 'capitalize' }}>{meeting.room_type}</td>
                <td style={styles.cell}>{meeting.participant_count}</td>
                <td style={{ ...styles.cell, textTransform: 'capitalize' }}>{meeting.state}</td>
                <td style={styles.cell}>{new Date(meeting.created_date).toLocaleString()}</td>
                <td style={styles.cell}>
                    <button onClick={() => navigate(`/meet/${meeting.meet_id}`)} style={{ ...styles.button, background: '#2563EB' }} aria-label={`Join meeting ${meeting.name}`}>Join</button>
                    {/* 4. The edit button now opens the modal */}
                    <button onClick={() => setIsEditModalOpen(true)} style={{ ...styles.button, background: '#F59E0B' }} aria-label={`Edit meeting ${meeting.name}`}>Edit</button>
                    {meeting.state === 'active' && <button onClick={() => handleStateChange('closed')} style={{ ...styles.button, background: '#EF4444' }} aria-label={`Close meeting ${meeting.name}`}>Close</button>}
                    {meeting.state === 'closed' && <button onClick={() => handleStateChange('active')} style={{ ...styles.button, background: '#10B981' }} aria-label={`Re-activate meeting ${meeting.name}`}>Activate</button>}
                </td>
            </tr>
        </>
    );
};


// The main dashboard component remains largely the same
const AdminDashboard = () => {
    const [meetings, setMeetings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { showAlert } = useAlert();

    const fetchMeetings = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await api.get('/admin/meetings');
            if (Array.isArray(response.data)) {
                setMeetings(response.data);
            } else {
                console.error("Admin dashboard received non-array data:", response.data);
                showAlert('Received invalid data from server.');
            }
        } catch (error) {
            showAlert(error.response?.data?.message || 'Failed to fetch meetings.');
        } finally {
            setIsLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchMeetings();
    }, [fetchMeetings]);

    if (isLoading) {
        return <div style={{ padding: '2rem' }}>Loading dashboard data...</div>;
    }

    const activeMeetings = meetings.filter(m => m.state === 'active');
    const closedMeetings = meetings.filter(m => m.state === 'closed');

    return (
        <div style={{ padding: '2rem' }}>
            <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1>Administrator Dashboard</h1>
                <button onClick={fetchMeetings} aria-label="Refresh meetings list">Refresh Data</button>
            </header>

            <section>
                <h2>Active Meetings ({activeMeetings.length})</h2>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr>
                            <th scope="col" style={{ padding: '0.5rem' }}>Name / ID</th>
                            <th scope="col" style={{ padding: '0.5rem' }}>Creator</th>
                            <th scope="col" style={{ padding: '0.5rem' }}>Type</th>
                            <th scope="col" style={{ padding: '0.5rem' }}>Participants</th>
                            <th scope="col" style={{ padding: '0.5rem' }}>State</th>
                            <th scope="col" style={{ padding: '0.5rem' }}>Created</th>
                            <th scope="col" style={{ padding: '0.5rem' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeMeetings.length > 0 ? (
                            activeMeetings.map(meeting => <MeetingRow key={meeting.meet_id} meeting={meeting} onUpdate={fetchMeetings} />)
                        ) : (
                            <tr><td colSpan="7" style={{ padding: '1rem', textAlign: 'center' }}>No active meetings.</td></tr>
                        )}
                    </tbody>
                </table>
            </section>

            <section style={{ marginTop: '3rem' }}>
                <h2>Closed & Archived Meetings ({closedMeetings.length})</h2>
                {/* Table for closed meetings would go here, similar to the active one */}
            </section>
        </div>
    );
};

export default AdminDashboard;
